
import { Label } from '@/model';
import labelStore, { labelSorter, labelTypeSorter } from '@/store/labels';
import { distinct, groupBy, notEmpty } from '@/util/array-util';
import Vue from 'vue';
import LabelChip from '@/components/LabelChip.vue';

interface Group {
  type: string;
  labels: readonly Label[];
}

interface Labels {
  labels: number[];
  isolate?: boolean;
}

export default Vue.extend({
  name: 'SelectionResultLabelPanel',
  components: { LabelChip },

  props: {
    slices: { type: Array as () => Array<Labels>, default: () => [] },
    groupLimit: { type: Number as () => number, default: 100 },
    closeableLimit: { type: Number as () => number, default: 64 },
  },

  data() {
    return {
      collapsedGroups: [] as string[],
    };
  },

  watch: {
    groups: {
      immediate: true,
      handler(groups) {
        this.collapsedGroups = groups
          .filter((g) => !!this.groupLimit && g.labels.reduce((acc, ls) => acc + ls.code.length, 0) > this.groupLimit)
          .map((g) => g.type);
      },
    },
  },

  computed: {
    commonLabelIds(): readonly number[] {
      return this.slices
        .map((slice) => slice.labels)
        .reduce((a, b) => a.filter((l) => b.includes(l)));
    },
    labelIds(): number[] {
      return distinct(this.slices.flatMap((s) => s.labels));
    },
    groups(): Group[] {
      const groups = groupBy(
        distinct(this.slices.flatMap((s) => s.labels))
          .map((lid) => this.label(lid))
          .filter(notEmpty),
        'type',
      );
      return Array.from(groups.keys())
        .sort(labelTypeSorter())
        .map((type) => ({
          type,
          labels: [...(groups.get(type) || [])].sort(labelSorter(['type', 'length', 'alpha'])),
        }));
    },
  },

  methods: {
    label(id: number): Label | undefined {
      return labelStore.getters.lookup.get(id);
    },
    labelText(label: Label): string {
      return this.commonLabelIds.includes(label.id) ? label.code : `[${label.code}]`;
    },
    open(group: Group) {
      return !this.collapsedGroups.includes(group.type);
    },
    closeable(group: Group) {
      return !!group.type && (group.labels.length > 1 || group.labels[0].code.length > this.closeableLimit);
    },
    toggleGroup(type: string) {
      if (this.collapsedGroups.includes(type)) {
        this.collapsedGroups = this.collapsedGroups.filter((t) => t !== type);
      } else {
        this.collapsedGroups = [...this.collapsedGroups, type];
      }
    },
    hoverLabels(labels: Label[], hover: boolean) {
      this.$emit('hoverLabels', labels, hover);
    },
  },
});
